/**
 * Persistent local storage cache for ticker details
 * Ticker details rarely change, so we can cache them for extended periods
 */

const CACHE_KEY = 'tickerDetailsCache';
const CACHE_VERSION = '1.0';
const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

class TickerDetailsCache {
  constructor() {
    this.cache = this.loadFromStorage();
  }

  /**
   * Load cache from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (!stored) return {};

      const parsed = JSON.parse(stored);
      
      // Check cache version and clear if outdated
      if (parsed.version !== CACHE_VERSION) {
        this.clearCache();
        return {};
      }

      // Clean up expired entries
      const now = Date.now();
      const cleaned = {};
      Object.keys(parsed.data || {}).forEach(ticker => {
        const entry = parsed.data[ticker];
        if (entry && entry.expiresAt > now) {
          cleaned[ticker] = entry;
        }
      });

      return cleaned;
    } catch (error) {
      console.warn('Failed to load ticker details cache:', error);
      return {};
    }
  }

  /**
   * Save cache to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        version: CACHE_VERSION,
        data: this.cache,
        lastUpdated: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save ticker details cache:', error);
    }
  }

  /**
   * Get ticker details from cache
   * @param {string} ticker - The ticker symbol
   * @returns {Object|null} - Cached ticker details or null if not found/expired
   */
  get(ticker) {
    if (!ticker) return null;

    const entry = this.cache[ticker.toUpperCase()];
    if (!entry) return null;

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      delete this.cache[ticker.toUpperCase()];
      this.saveToStorage();
      return null;
    }

    return entry.data;
  }

  /**
   * Set ticker details in cache
   * @param {string} ticker - The ticker symbol
   * @param {Object} details - The ticker details to cache
   * @param {number} ttl - Time to live in milliseconds (optional, defaults to 24 hours)
   */
  set(ticker, details, ttl = DEFAULT_TTL) {
    if (!ticker || !details) return;

    const normalizedTicker = ticker.toUpperCase();
    const expiresAt = Date.now() + ttl;

    this.cache[normalizedTicker] = {
      data: details,
      expiresAt,
      cachedAt: Date.now()
    };

    this.saveToStorage();
  }

  /**
   * Check if ticker details are cached and not expired
   * @param {string} ticker - The ticker symbol
   * @returns {boolean} - True if cached and not expired
   */
  has(ticker) {
    if (!ticker) return false;

    const entry = this.cache[ticker.toUpperCase()];
    if (!entry) return false;

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      delete this.cache[ticker.toUpperCase()];
      this.saveToStorage();
      return false;
    }

    return true;
  }

  /**
   * Remove specific ticker from cache
   * @param {string} ticker - The ticker symbol to remove
   */
  remove(ticker) {
    if (!ticker) return;

    delete this.cache[ticker.toUpperCase()];
    this.saveToStorage();
  }

  /**
   * Clear all cached ticker details
   */
  clearCache() {
    this.cache = {};
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.warn('Failed to clear ticker details cache:', error);
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache statistics
   */
  getStats() {
    const now = Date.now();
    const entries = Object.values(this.cache);
    
    return {
      totalEntries: entries.length,
      expiredEntries: entries.filter(entry => entry.expiresAt <= now).length,
      validEntries: entries.filter(entry => entry.expiresAt > now).length,
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(entry => entry.cachedAt)) : null,
      newestEntry: entries.length > 0 ? Math.max(...entries.map(entry => entry.cachedAt)) : null
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup() {
    const now = Date.now();
    let cleaned = false;

    Object.keys(this.cache).forEach(ticker => {
      if (this.cache[ticker].expiresAt <= now) {
        delete this.cache[ticker];
        cleaned = true;
      }
    });

    if (cleaned) {
      this.saveToStorage();
    }
  }
}

// Create singleton instance
const tickerDetailsCache = new TickerDetailsCache();

// Clean up expired entries on initialization
tickerDetailsCache.cleanup();

export default tickerDetailsCache;
